import { useState, useEffect, Fragment } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { stateStartLeaf, stateHintSentence, stateRevealQuestions, stateRevealResults, stateAnswerQuestion } from "../store";
import Loading from '../components/Loading';
import pageStyles from '../styles/page-common.module.css';
import leafStyles from '../styles/page-leaf.module.css';
import { INITIAL_HINT_COUNT, LANGUAGE_NAMES } from "../constants";
import kanjiLists from "../kanjiLists";
import { getTree } from '../backend';


function ExplanationBlock({sentence, textLanguage, userLanguage, onHide}) {
  
  const translation = sentence.translations[userLanguage];
  const breakdown = sentence.breakdown[userLanguage];
  const notes = [...sentence.grammar_notes[userLanguage], ...sentence.idiom_notes[userLanguage]]
  const transliteration = sentence.transliteration;
  const kana = sentence.kana;

  return (
    <div className={leafStyles.explanationBlock}>
      {
        textLanguage === 'hi' && (
          <>
            <p><strong>Pronunciation</strong></p>
            <p lang={textLanguage} translate="no" className="notranslate">{transliteration}</p>
            <br/>
          </>
        )
      }
      {
        textLanguage === 'ja' && (
          <>
            <p><strong>Kana</strong></p>
            <p lang={textLanguage} translate="no" className="notranslate">{kana}</p>
            <br/>
          </>
        )
      }
      <p><strong>Translation:</strong></p>
      <p lang={userLanguage} translate="no" className="notranslate">{translation}</p>
      <br/>
      <p><strong>Breakdown:</strong></p>
      <ul>
        {breakdown.map((item, index) => (
          <li key={index}>
            <strong lang={textLanguage} translate="no" className="notranslate">{item.text}</strong>: ({textLanguage === 'ja' && item.kana !== item.text && <span lang={textLanguage} translate="no" className="notranslate">{`${item.kana}; `}</span>}<span lang={userLanguage} translate="no" className="notranslate">{item.category}</span>) <span lang={userLanguage} translate="no" className="notranslate">{item.meaning}</span>
          </li>
        ))}
      </ul>
      <br/>
      {
        notes.length !== 0 && (
          <>
            <p><strong>Notes</strong></p>
            <ul>
              {notes.map((item, index) => <li key={index} lang={userLanguage} translate="no" className="notranslate">{item}</li>)}
            </ul>
            <br/>
          </>
        )
      }
      <p className={leafStyles.hideExplanationLine} onClick={onHide}>Hide Explanation</p>
    </div>
  )
} 

// paragraph component, shows text of paragraph and explanation block below if sentence within paragraph clicked
const Paragraph = ({ paragraph, selectedSentence, setSelectedSentence, treeRecordID, leafOrder, leafRecord, textLanguage, userLanguage, level }) => {

  return (
    <div>
      {/* text block */}
      <p>
        {paragraph.sentences.map((sentence, index) => (
          <Fragment key={index}>
            <Sentence
              sentence={sentence}
              isSelected={selectedSentence === sentence}
              setSelectedSentence={setSelectedSentence}
              treeRecordID={treeRecordID}
              leafOrder={leafOrder}
              paragraphOrder={paragraph.paragraph_order}
              leafRecord={leafRecord}
              lang={textLanguage}
              level={level}
            />
            {index < paragraph.sentences.length - 1 && ' '}
          </Fragment>
        ))}
      </p>

      {/* explanation block */}
      {selectedSentence &&
        paragraph.sentences.includes(selectedSentence) && <ExplanationBlock sentence={selectedSentence} textLanguage={textLanguage} userLanguage={userLanguage} onHide={() => setSelectedSentence(null)}/>
      }
    </div>
  );
};


// sentence component, shows text of sentence
const Sentence = ({ sentence, isSelected, setSelectedSentence, treeRecordID, leafOrder, paragraphOrder, leafRecord, lang, level }) => {

  // whether this sentence in list of previous hints
  const sentenceOrder = sentence.sentence_order;
  const prevHinted = leafRecord.hintedSentences.some(s=>s.paragraphOrder === paragraphOrder && s.sentenceOrder===sentenceOrder)

  let clickable = false;
  if (leafRecord.resultsRevealed) {
    clickable = true;
  } else if (leafRecord.questionsRevealed) {
    clickable = false;
  } else if (prevHinted || (INITIAL_HINT_COUNT - leafRecord.hintedSentences.length) > 0) {
    clickable = true;
  } 

  const onClick = () => {
    // if in pre-questions phase and clicking new sentence (not selected or prev hinted), add sentence to hint list
    if (!isSelected && !leafRecord.resultsRevealed && !prevHinted) {
      stateHintSentence(treeRecordID, leafOrder, paragraphOrder, sentenceOrder);
    }
    setSelectedSentence(isSelected ? null : sentence)
  }

  let className = leafStyles.sentence;
  if (isSelected) className += ` ${leafStyles.sentenceSelected}`;
  if (clickable) {
    className += ` ${leafStyles.sentenceClickable}`;
    if (prevHinted && !leafRecord.resultsRevealed) {
      className += ` ${leafStyles.sentencePrevClickable}`
    }
  }

  const renderJapaneseText = () => {
    const result = [];
    sentence.kakasi.forEach((token, ind) => {
      // if token is pure katakana or hiragana or punctuation, return original
      if((token.orig === token.kata) || (token.orig === token.hira)) {
        result.push(<span className={`notranslate ${className}`} key={ind}>{token.orig}</span>)
      }
      // else, check to see if kanji all easy, otherwise set up furigana 
      else {
        const kanjiMatches = token.orig.match(/[\p{Script=Han}]/gu) || [];
        const allEasy = kanjiMatches.every(kanji => kanjiLists[level].has(kanji));
        if (allEasy) {
          result.push(<span key={ind} className={`notranslate ${className}`}>{token.orig}</span>)
        } else {
          result.push(
            <ruby key={ind} className={`notranslate ${className}`}>
              {token.orig}
              <rt>{token.hira}</rt>
            </ruby>
          )
        }
      }
    })
    return result;
  }


  return (
    <span onClick={clickable ? onClick : null} lang={lang} translate="no" className={`notranslate ${className}`}>
      {lang !== 'ja' ? sentence.text : renderJapaneseText()}
    </span>
  );
};


// question box component, shows checkbox questions, when submitted also shows answers
const Questions = ({ questions, userAnswers, treeRecordID, leafOrder, showResults, onSubmit, userLanguage }) => {
  
  // handler for form change
  const handleAnswerChange = (questionIndex, choiceIndex) => {
    stateAnswerQuestion(treeRecordID, leafOrder, questionIndex, choiceIndex);
  };

  // boolean: whether all questions answered
  const allAnswered = userAnswers.every((answer) => answer !== null);

  return (
    <div className={leafStyles.questionsContainer}>

      {/* questions */}
      {questions.map((question, index) => (
        <div key={index} className={leafStyles.question}>
          <p className={leafStyles.questionText}>{question.text[userLanguage]}</p>
          {question.options[userLanguage].map((option, choiceIndex) => {
            const isCorrectAnswer = showResults && choiceIndex === question.correct_answer;
            const isUserAnswer = showResults && userAnswers[index] === choiceIndex;
            let className = leafStyles.choiceLabel
            if (showResults && isCorrectAnswer) className += ` ${leafStyles.correctAnswer}`
            return (
              <label key={choiceIndex} className={className}>
                <input
                  type="radio"
                  name={`question-${index}`}
                  checked={userAnswers[index] === choiceIndex}
                  onChange={() => handleAnswerChange(index, choiceIndex)}
                  disabled={showResults}
                />
                {option}
                {showResults && isUserAnswer && <span className={leafStyles.answerFeedback}>{isCorrectAnswer ? "✔️" : "❌"}</span>}
              </label>
            );
          })}
        </div>
      ))}
      
      {/* submit answers button */}
      {!showResults && <button onClick={onSubmit} disabled={!allAnswered}>Check Answers</button>}

    </div>
  );
};


// main page component
function Leaf() {

  // get treeID and leaf index from URL
  let { treeId, leafOrder } = useParams();
  leafOrder = parseInt(leafOrder, 10);

  // get tree and leaf index from passed state props
  const location = useLocation();

  // get tree data from redux state
  const treeRecords = useSelector((state) => state.treeRecords)

  // states
  const [tree, setTree] = useState(location.state?.tree || null);
  const [leaf, setLeaf] = useState(null);
  const [error, setError] = useState('');
  const [leafRecord, setLeafRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [score, setScore] = useState(null);
  const [leafStateStarted, setLeafStateStarted] = useState(false);
  const [userLanguage, setUserLanguage] = useState(null);

  // fetch tree if not passed in state
  useEffect(() => {
    async function fetchTree() {
      try {
        setLoading(true);
        const response = await getTree({ tree_id: treeId });
        setTree(response);
        setError('');
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError('Tree not found.');
        } else {
          setError('Some error occurred.');
        }
      }
    }

    if (!tree) {
      fetchTree();
    }
  }, [treeId, tree]);

  // when tree is loaded, set leaf
  useEffect(() => {
    if (tree) {
      if (leafOrder >= 0 && leafOrder < tree.leaves.length) {
        setLeaf(tree.leaves[leafOrder]);
        setUserLanguage(tree.translation_languages[0]);
      } else {
        setError('Leaf not found.');
      }
    }
  }, [tree, leafOrder]);

  // on mount, effect chain:
  // start leaf in redux state -> get current leaf record -> finish loading
  useEffect(() => {
    if(tree && tree._id && leaf && !leafStateStarted) {
      stateStartLeaf(tree._id, leafOrder, leaf.questions.length);
      setLeafStateStarted(true);
    }
  }, [tree, leaf, leafOrder, leafStateStarted])
  useEffect(() => {
    if (tree && treeRecords[tree._id] && treeRecords[tree._id].leaves[leafOrder]) {
      setLeafRecord(treeRecords[tree._id].leaves[leafOrder]);
    }
  }, [tree, treeRecords, leafOrder])
  useEffect(() => {
    if(tree && leafRecord) {
      setLoading(false);
    }
  }, [tree, leafRecord])

  // when results are revealed (whether on mount or on submit), calculate results
  useEffect(() => {
    if (leafRecord && leafRecord.resultsRevealed) {
      const calculatedResults = leaf.questions.map(
        (question, index) => leafRecord.userAnswers[index] === question.correct_answer
      );
      setScore(calculatedResults.filter(Boolean).length);
    }
  }, [leafRecord, leaf])

  // handler for show questions button: deselect any sentences and set show state
  const handleShowQuestions = () => {
    setSelectedSentence(null);
    stateRevealQuestions(tree._id, leafOrder);
  };

  // handler for submit answers button
  const handleSubmit = () => {
    stateRevealResults(tree._id, leafOrder);
  };

  // if page still loading return "loading" component
  if (loading) return <Loading/>;
  if (error) return <div className={pageStyles.styledPage}><p>{error}</p></div>;
  if (!leaf) return <div className={pageStyles.styledPage}><p>Leaf not found.</p></div>;

  return (
    <>
      <Helmet>
        <title>{`${leafOrder + 1}. ${leaf.title}`} | LingoLeaf</title>
      </Helmet>
      <div className={pageStyles.styledPageLeftAligned}>
        {/* chapter title */}
        <h1 lang={tree.language} translate="no" className={`notranslate ${leafStyles.treeTitle}`}>{tree.title}</h1>
        <div className={leafStyles.topSection}>
          {tree.language === 'ja' && <span lang={tree.language} translate="no" className="notranslate">({tree.title_kana})</span>}
          <p>Translation Language:</p>
          <select
            name="translationLanguage"
            value={userLanguage}
            onChange={(e) => setUserLanguage(e.target.value)}
            className={pageStyles.dropdown}
          >
            {Object.values(tree.translation_languages).map((lang) => (
              <option key={lang} value={lang}>
                {LANGUAGE_NAMES[lang]}
              </option>
            ))}
          </select>
        </div>
        <hr className={pageStyles.hrule} />
        {!leafRecord.questionsRevealed && <div className={`${pageStyles.alertBanner} ${leafStyles.hintContainer}`}> <p>Remaining hints: {INITIAL_HINT_COUNT - leafRecord.hintedSentences.length}</p></div>}
        <h2 lang={tree.language} translate="no" className="notranslate">{leafOrder + 1}. {leaf.title} {tree.language === 'ja' && <span lang={tree.language} translate="no" className="notranslate">({leaf.title_kana})</span>}</h2>
        
        {/* paragraph components */}
        {leaf.paragraphs.map((paragraph, index) => (
          <Paragraph
            key={index}
            paragraph={paragraph}
            selectedSentence={selectedSentence}
            setSelectedSentence={setSelectedSentence}
            treeRecordID={tree._id}
            leafOrder={leafOrder}
            leafRecord={leafRecord}
            textLanguage={tree.language}
            userLanguage={userLanguage}
            level={tree.level}
          />
        ))}

        <hr className={pageStyles.hrule} />

        {/* 'show questions' button (disappears when pressed */}
        {!leafRecord.questionsRevealed && <button onClick={handleShowQuestions}>Show Questions</button>}

        {/* questions */}
        {leafRecord.questionsRevealed && (
          <Questions
            questions={leaf.questions}
            userAnswers={leafRecord.userAnswers}
            treeRecordID={tree._id}
            leafOrder={leafOrder}
            showResults={leafRecord.resultsRevealed}
            onSubmit={handleSubmit}
            userLanguage={userLanguage}
          />
        )}
        
        {/* total score */}
        {leafRecord.resultsRevealed && <span className={leafStyles.scoreLabel}>Total Score: {score} / {leaf.questions.length}</span>}
        
        {/* collect leaf button */}
        {!leafRecord.complete && leafRecord.resultsRevealed && <NavLink to='/leaf-complete' state={{ tree, leafOrder, score }} style={{display: 'contents'}}><button>Collect Leaf</button></NavLink>}
        {leafRecord.complete && <button disabled={true}>Leaf collected!</button>}

        {/* return to tree */}
        <NavLink to={`/tree/${tree._id}`} style={{display: 'contents'}}><button>Return to Tree</button></NavLink>

        <NavLink style={{display: 'contents'}} to='/contact'><span className={leafStyles.reportLink}>Report inaccurate or inappropriate content</span></NavLink>
      </div>
    </>
    
  );
}

export default Leaf;